import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { FlexRow, FlexBaseCol, P, H4 } from '@contently/toolkit';
import styled from '@emotion/styled';
import Form from '../components/IntakeForm';
import CallToAction from '../components/CallToAction';
import useLocalStorage from '../utils/hooks/useLocalStorage';

import logoSrc from '../logo.svg';
import dellLogo from '../logos/dell.png';
import googleLogo from '../logos/google.png';
import marriottLogo from '../logos/marriott.png';
import royalbankLogo from '../logos/royalbank.png';
import walmartLogo from '../logos/walmart.png';

const LogoWrapper = styled('div')`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  margin-top: 9%;
  opacity: 0.8;
  pointer-events: none;
  img {
    width: 45px;
    filter: grayscale(100%);
  }
  &:nth-of-type(1) {
    margin-top: 0;
  }
  &:nth-of-type(2) {
    margin-left: 8%;
  }
  &:nth-of-type(3) {
    margin-left: 16%;
  }
  &:nth-of-type(4) {
    margin-left: 24%;
  }
  &:nth-of-type(5) {
    margin-left: 32%;
  }
`;

const LogosInnerWrapper = styled('div')`
  position: fixed;
  right: 0;
  left: 70%;
  @media screen and (max-width: 639px) {
    display: none;
  }
`;

const LogosContainer = styled('div')`
  pointer-events: none;
  position: fixed;
  right: 0;
  left: 0;
  top: -1000px;
  bottom: -1000px;
  z-index: -1;
  background: #e8f7fa;
  clip-path: polygon(40% 0%, 100% 0%, 100% 100%, 90% 100%);
  display: flex;
  align-items: center;
  @media screen and (max-width: 639px) {
    opacity: 0.5;
    clip-path: polygon(40% 0%, 100% 0%, 100% 100%, 90% 100%);
  }
`;

const ContentlyLogo = styled('img')`
  height: 30px;
`;

const FlexRowIntroWrapper = styled(FlexRow)`
  margin-bottom: 30px;
  h1 {
    font-size: 24px;
    font-weight: 400;
    margin: 7% 0 10px 0;
  }
  ${P} {
    font-size: 0.8125rem;
    font-weight: 300;
    line-height: 1.5;
  }
`;

const FlexRowForm = styled(FlexRow)`
  width: 100%;
`;

const GridContainer = styled('div')`
  display: grid;
  grid-template-columns: [first] 65px [form] auto [bleed] 40% [last];
  grid-template-rows: [first] 0 [form] auto [bleed] 0 [last];
  min-height: 100vh;
  @media screen and (max-width: 639px) {
    grid-template-columns: [first] 30px [form] auto [bleed] 30px [last];
  }
`;

const ContentWrapper = styled('div')`
  grid-column: form / bleed;
  grid-row: form / bleed;
  max-width: 600px;
  align-self: center;
  justify-self: center;
  margin: 65px 0;
`;

const ErrorWrapper = styled('div')`
  font-weight: 300;
  ${H4} {
    margin-top: 20px;
    margin-bottom: 10px;
  }
`;

const FormPage = ({ history, location }) => {
  const [error, setError] = useState(false);
  const handleError = () => setError(true);
  const setDirectCustomer = useLocalStorage('directCustomer', false)[1];
  const queryValues = queryString.parse(location.search);
  setDirectCustomer(!!queryValues.email);

  let formContent = (
    <>
      <FlexRowIntroWrapper margin={{ vertical: 'medium' }}>
        <h1>Find world-class talent for your content</h1>
        <P>
          As a Contently customer, you’ll receive access to our global network of 140,000+ vetted
          and trained freelancers. To see first-hand how we can help you, we are offering a
          <b> free preview</b> of our Intelligent Talent Recommendations algorithm! Tell us who you
          are and what you need, and we’ll find the right collaborators for you.
        </P>
      </FlexRowIntroWrapper>
      <FlexRowForm>
        <Form queryValues={queryValues} onError={handleError} />
      </FlexRowForm>
    </>
  );

  if (error || (location.state && location.state.error)) {
    formContent = (
      <>
        <ErrorWrapper>
          <H4>
            Sorry for the trouble, but something went wrong on our end. Please try again later.
          </H4>
          <CallToAction />
        </ErrorWrapper>
      </>
    );
  }

  useEffect(() => {
    if (location.state && location.state.error) {
      setError(true);
      history.replace({
        pathname: '/',
        state: {}
      });
    }
  }, [history, location.state]);

  return (
    <GridContainer>
      <ContentWrapper>
        <FlexRow>
          <FlexBaseCol>
            <ContentlyLogo src={logoSrc} />
          </FlexBaseCol>
        </FlexRow>
        <FlexRow>{formContent}</FlexRow>

        <LogosContainer>
          <LogosInnerWrapper>
            <LogoWrapper>
              <img src={dellLogo} alt="Dell" />
            </LogoWrapper>

            <LogoWrapper>
              <img src={googleLogo} alt="Google" />
            </LogoWrapper>

            <LogoWrapper>
              <img src={marriottLogo} alt="Marriott" />
            </LogoWrapper>

            <LogoWrapper>
              <img src={royalbankLogo} alt="Royal Bank" />
            </LogoWrapper>

            <LogoWrapper>
              <img src={walmartLogo} alt="Walmart" />
            </LogoWrapper>
          </LogosInnerWrapper>
        </LogosContainer>
      </ContentWrapper>
    </GridContainer>
  );
};

FormPage.propTypes = {
  history: PropTypes.shape({
    replace: PropTypes.func
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      error: PropTypes.bool
    }),
    search: PropTypes.string
  }).isRequired
};

export default withRouter(FormPage);
